import { createAsyncThunk, AnyAction } from "@reduxjs/toolkit"
import { ThunkDispatch } from "redux-thunk"

import i18n from "config/i18n"
import { API } from "redux/api"
import {
  ACTION_STATUS_SUCCESS,
  POLLING_INTERVAL,
  POLLING_TIMEOUT,
} from "config"
import { fetchDebts } from "redux/features/debt/debtThunk"
import {
  start as startPolling,
  reset as resetPolling,
} from "redux/features/polling/pollingSlice"
import { updateDialog } from "redux/features/dialog/dialogSlice"
import { PaymentScheduleValues } from "types"

export const fetchPaymentSchedule = createAsyncThunk(
  "payment-schedule/fetch",
  async (values: { payment_schedule_id: string }) => {
    const response = await API.get(
      `/payment_schedules/${values.payment_schedule_id}`,
    )
    return response.data
  },
)

export const createPaymentScheduleSession = createAsyncThunk(
  "payment-schedule-session/create",
  async (values: PaymentScheduleValues) => {
    const response = await API.post("/payment_schedules", values)
    return response.data
  },
)

export const fetchPaymentScheduleSession = createAsyncThunk(
  "payment-schedule-session/fetch",
  async (values: { session_id: string }) => {
    const response = await API.get(
      `/payment_schedules/session/${values.session_id}`,
    )

    return response.data
  },
)

export const makePaymentSchedule = (values: PaymentScheduleValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise<{ payment_schedule_id: string }>((resolve, reject) => {
      return dispatch(createPaymentScheduleSession(values))
        .then(
          ({ payload }) => {
            if (!payload || !payload.payment_schedule) {
              return reject(
                i18n.t("notice.payment_schedule.session.unavailable"),
              )
            }

            let payment_schedule_id: string

            const timerId = setInterval(async () => {
              const { session_id } = payload.payment_schedule
              const result = await dispatch(
                fetchPaymentScheduleSession({ session_id }),
              )

              if (result?.payload?.session) {
                const { session } = result.payload

                if (session?.status === ACTION_STATUS_SUCCESS) {
                  const { debt_id } = values
                  payment_schedule_id =
                    session.action_payload.payment_schedule_id

                  dispatch(fetchPaymentSchedule({ payment_schedule_id })).then(
                    ({ payload }) => {
                      const { payment_schedule } = payload

                      if (
                        payment_schedule &&
                        payment_schedule.payment_schedule_invoices.length > 0
                      ) {
                        // NOTE: clear interval when status = created and invoices are synced
                        clearInterval(timerId)
                        dispatch(resetPolling())

                        // NOTE: fetch Debts to update page content
                        dispatch(fetchDebts()).then(() => {
                          // NOTE: fetch PS to update modal content
                          dispatch(
                            updateDialog({
                              content: {
                                debtId: debt_id,
                                paymentScheduleId: payment_schedule_id,
                              },
                            }),
                          )
                        })
                      }
                    },
                  )

                  resolve({ payment_schedule_id })
                }
              } else {
                dispatch(resetPolling())
                clearInterval(timerId)

                reject(result.payload.error)
              }
            }, POLLING_INTERVAL)

            dispatch(startPolling({ pollingId: timerId }))

            setTimeout((payment_schedule_id: string) => {
              clearInterval(timerId)
              dispatch(resetPolling())
              if (!payment_schedule_id) {
                reject(i18n.t("notice.payment_schedule.create.failure"))
              }
            }, POLLING_TIMEOUT)
          },
          () => {
            reject(i18n.t("notice.payment_schedule.session.failure"))
          },
        )
        .catch((error) => {
          reject(error)
        })
    })
  }
}
